import { useAuditLogs } from '../model/useAuditLogQueries';
import { formatDateTimeFull, formatDateTime } from '@/shared/lib/format';
import * as s from './AuditLogSection.css';

interface AuditLogSectionProps {
  attendanceId: string;
}

const ACTION_LABEL = {
  CLOCK_IN: '출근',
  CLOCK_OUT: '퇴근',
  CORRECTION_REQUESTED: '정정 요청',
  CORRECTION_RESUBMITTED: '정정 재신청',
  CORRECTION_APPROVED: '정정 승인',
  CORRECTION_REJECTED: '정정 거부',
  ADMIN_EDIT: '관리자 수정',
} as const satisfies Record<string, string>;

const FIELD_LABEL = {
  clockInAt: '출근 시간',
  clockOutAt: '퇴근 시간',
  note: '메모',
  status: '상태',
} as const satisfies Record<string, string>;

function tryFormatValue(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (!isNaN(date.getTime()) && value.includes('T')) {
    return formatDateTime(value);
  }
  return value;
}

export function AuditLogSection({ attendanceId }: AuditLogSectionProps) {
  const { data: logs, isLoading, isError } = useAuditLogs(attendanceId);

  if (isLoading) {
    return <p className={s.stateMessage}>감사 로그를 불러오는 중...</p>;
  }

  if (isError) {
    return <p className={s.errorMessage}>감사 로그를 불러오지 못했습니다.</p>;
  }

  if (!logs || logs.length === 0) {
    return <p className={s.stateMessage}>감사 로그가 없습니다.</p>;
  }

  return (
    <div>
      <h3 className={s.heading}>변경 이력</h3>
      <ol className={s.list}>
        {logs.map((log, index) => {
          const isLast = index === logs.length - 1;
          return (
            <li key={log.id} className={s.listItem}>
              {/* Timeline track */}
              <div className={s.timelineTrack}>
                <div className={s.timelineDot} />
                {!isLast && <div className={s.timelineLine} />}
              </div>

              {/* Content */}
              <div className={`${s.entryContent}${!isLast ? ` ${s.entryContentSpaced}` : ''}`}>
                <div className={s.entryHeader}>
                  <span className={s.actionLabel}>
                    {ACTION_LABEL[log.action as keyof typeof ACTION_LABEL] ?? log.action}
                  </span>
                  {log.fieldName && (
                    <span className={s.fieldLabel}>
                      ({FIELD_LABEL[log.fieldName as keyof typeof FIELD_LABEL] ?? log.fieldName})
                    </span>
                  )}
                  <span className={s.timestamp}>{formatDateTimeFull(log.createdAt)}</span>
                </div>

                {(log.oldValue !== null || log.newValue !== null) && (
                  <p className={s.valueChange}>
                    <span className={s.oldValue}>{tryFormatValue(log.oldValue)}</span>
                    {' → '}
                    <span className={s.newValue}>{tryFormatValue(log.newValue)}</span>
                  </p>
                )}

                {log.reason && <p className={s.reason}>사유: {log.reason}</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
